import { tx as translateInterfaceText } from '@/utils/i18n';
import React from 'react';

interface AppErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔥 [AppErrorBoundary] Erro capturado:', error);
    console.error('📍 Stack trace:', error.stack);
    console.error('⚛️ Component stack:', errorInfo.componentStack);
    
    // Diagnóstico de múltiplas instâncias do React
    const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (hook?.renderers) {
      const rendererCount = hook.renderers.size;
      console.error(`🔍 Número de React renderers detectados: ${rendererCount}`);
      if (rendererCount > 1) {
        console.error('⚠️ MÚLTIPLAS INSTÂNCIAS DO REACT DETECTADAS - Esta é provavelmente a causa do erro #310');
      }
    }
    
    // Log de contexto
    console.error('🌐 Context:', {
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      reactVersion: React.version,
    });
    
    // Log específico para erro #310
    if (error.message.includes('#310') || error.message.includes('Invalid hook call')) {
      console.error('❗ React Error #310 detectado');
      console.error('💡 Possível causa: hook condicional, localStorage no render, ou múltiplas versões do React');
      console.error('🔍 Verifique: useState/useEffect em condicionais, acesso direto a window/localStorage');
    }
    
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHardReload = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      console.warn('Falha ao limpar cache/SW antes do hard reload:', e);
    }
    const url = new URL(window.location.href);
    url.searchParams.set('_', Date.now().toString());
    window.location.replace(url.toString());
  };

  handleCopyError = () => {
    const errorDetails = `
=== Error Details ===
Name: ${this.state.error?.name}
Message: ${this.state.error?.message}

=== Stack Trace ===
${this.state.error?.stack}

=== Component Stack ===
${this.state.errorInfo?.componentStack}

=== Context ===
URL: ${window.location.href}
React Version: ${React.version}
React Renderers: ${(() => { try { const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__; return hook?.renderers ? hook.renderers.size : 'unknown'; } catch { return 'unknown'; } })()}
User Agent: ${navigator.userAgent}
    `.trim();
    
    navigator.clipboard.writeText(errorDetails).then(() => {
      alert(translateInterfaceText("Detalhes do erro copiados para a área de transferência!"));
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <div className="text-6xl">⚠️</div>
            <h1 className="text-2xl font-bold text-foreground">
              {translateInterfaceText("Algo deu errado")}</h1>
            <p className="text-muted-foreground">
              {translateInterfaceText("Ocorreu um erro inesperado. Por favor, recarregue a página.")}</p>
            {this.state.error && (
              <details open className="text-left text-xs text-muted-foreground bg-muted p-3 rounded-md max-h-64 overflow-auto">
                <summary className="cursor-pointer font-medium mb-2">
                  {translateInterfaceText("Detalhes do erro (aberto por padrão)")}</summary>
                <div className="space-y-2 mt-2">
                  <div>
                    <strong>{translateInterfaceText("Nome:")}</strong> {this.state.error.name}
                  </div>
                  <div>
                    <strong>{translateInterfaceText("Mensagem:")}</strong>
                    <pre className="mt-1 whitespace-pre-wrap">{this.state.error.message}</pre>
                  </div>
                  <div>
                    <strong>{translateInterfaceText("Stack Trace:")}</strong>
                    <pre className="mt-1 whitespace-pre-wrap">{this.state.error.stack}</pre>
                  </div>
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>{translateInterfaceText("Component Stack:")}</strong>
                      <pre className="mt-1 whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                    </div>
                  )}
                  <div>
                    <strong>{translateInterfaceText("React Version:")}</strong> {React.version}
                  </div>
                  <div>
                    <strong>{translateInterfaceText("React Renderers:")}</strong> {(() => {
                      try {
                        const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
                        return hook?.renderers ? hook.renderers.size : translateInterfaceText("indisponível");
                      } catch {
                        return translateInterfaceText("indisponível");
                      }
                    })()}
                  </div>
                </div>
              </details>
            )}
            <div className="flex gap-2">
              <button
                onClick={this.handleCopyError}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-colors"
              >
                {translateInterfaceText("Copiar Detalhes")}</button>
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                {translateInterfaceText("Recarregar Página")}</button>
              <button
                onClick={this.handleHardReload}
                className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-colors"
              >
                {translateInterfaceText("Recarregar (sem cache)")}</button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
